// AGC: Mock management plans based on EMBRAPA/INTA recommendations
// AGC: Simulated agronomic tasks for different crops

import { CropType } from '../types'

export interface ManagementTask {
  id: string
  name: string
  description: string
  daysFromStart: number // AGC: Days from reference date (planting)
  order: number
  status: 'pendente' | 'em_andamento' | 'concluida'
  taskType: 'preparo' | 'plantio' | 'aplicacao' | 'colheita' | 'outro'
}

/**
 * AGC: Generate management plan for a crop
 * AGC: Based on simplified EMBRAPA/INTA recommendations
 */
export function generateManagementPlan(cropType: CropType, cropName: string): ManagementTask[] {
  const plans: Record<CropType, ManagementTask[]> = {
    soja: [
      {
        id: 't1',
        name: 'Descompactação do solo',
        description: 'Romper camadas compactadas do solo para melhorar infiltração de água',
        daysFromStart: -30,
        order: 1,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't2',
        name: 'Preparo de solo',
        description: 'Preparo convencional ou plantio direto',
        daysFromStart: -15,
        order: 2,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't3',
        name: 'Aplicação de herbicida pré-emergente',
        description: 'Controle de plantas daninhas antes da emergência',
        daysFromStart: -3,
        order: 3,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't4',
        name: 'Semeadura',
        description: 'Plantio de soja com espaçamento adequado',
        daysFromStart: 0,
        order: 4,
        status: 'pendente',
        taskType: 'plantio',
      },
      {
        id: 't5',
        name: 'Adubação de cobertura',
        description: 'Aplicação de fertilizantes nitrogenados',
        daysFromStart: 25,
        order: 5,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't6',
        name: 'Aplicação de fungicida 1',
        description: 'Controle preventivo de doenças - estádio V6',
        daysFromStart: 40,
        order: 6,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't7',
        name: 'Aplicação de fungicida 2',
        description: 'Controle preventivo de doenças - estádio R2',
        daysFromStart: 60,
        order: 7,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't8',
        name: 'Colheita',
        description: 'Colheita quando grãos atingirem 13-15% de umidade',
        daysFromStart: 120,
        order: 8,
        status: 'pendente',
        taskType: 'colheita',
      },
    ],
    milho: [
      {
        id: 't1',
        name: 'Preparo de solo',
        description: 'Aração e gradagem ou plantio direto',
        daysFromStart: -20,
        order: 1,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't2',
        name: 'Aplicação de herbicida pré-plantio',
        description: 'Controle de plantas daninhas',
        daysFromStart: -5,
        order: 2,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't3',
        name: 'Semeadura + Adubação de base',
        description: 'Plantio com fertilização NPK no sulco',
        daysFromStart: 0,
        order: 3,
        status: 'pendente',
        taskType: 'plantio',
      },
      {
        id: 't4',
        name: 'Aplicação de herbicida pós-emergente',
        description: 'Controle de plantas daninhas emergidas',
        daysFromStart: 20,
        order: 4,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't5',
        name: 'Adubação de cobertura (N)',
        description: 'Aplicação de nitrogênio - estádio V4-V6',
        daysFromStart: 30,
        order: 5,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't6',
        name: 'Aplicação de inseticida',
        description: 'Controle de lagarta-do-cartucho',
        daysFromStart: 45,
        order: 6,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't7',
        name: 'Aplicação de fungicida',
        description: 'Controle preventivo de doenças foliares',
        daysFromStart: 60,
        order: 7,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't8',
        name: 'Colheita',
        description: 'Colheita quando grãos atingirem 18-20% de umidade',
        daysFromStart: 140,
        order: 8,
        status: 'pendente',
        taskType: 'colheita',
      },
    ],
    trigo: [
      {
        id: 't1',
        name: 'Preparo de solo',
        description: 'Preparo adequado para época de inverno',
        daysFromStart: -15,
        order: 1,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't2',
        name: 'Semeadura + Adubação',
        description: 'Plantio com adubação NPK',
        daysFromStart: 0,
        order: 2,
        status: 'pendente',
        taskType: 'plantio',
      },
      {
        id: 't3',
        name: 'Aplicação de herbicida',
        description: 'Controle de plantas daninhas',
        daysFromStart: 25,
        order: 3,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't4',
        name: 'Adubação de cobertura',
        description: 'Aplicação de nitrogênio - perfilhamento',
        daysFromStart: 35,
        order: 4,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't5',
        name: 'Aplicação de fungicida 1',
        description: 'Controle preventivo - início do espigamento',
        daysFromStart: 70,
        order: 5,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't6',
        name: 'Aplicação de fungicida 2',
        description: 'Controle preventivo - floração',
        daysFromStart: 85,
        order: 6,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't7',
        name: 'Colheita',
        description: 'Colheita quando grãos estiverem maduros',
        daysFromStart: 130,
        order: 7,
        status: 'pendente',
        taskType: 'colheita',
      },
    ],
    cafe: [
      {
        id: 't1',
        name: 'Preparo da cova',
        description: 'Abertura e adubação de covas para plantio',
        daysFromStart: -30,
        order: 1,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't2',
        name: 'Plantio de mudas',
        description: 'Transplante de mudas para o campo',
        daysFromStart: 0,
        order: 2,
        status: 'pendente',
        taskType: 'plantio',
      },
      {
        id: 't3',
        name: 'Adubação de formação',
        description: 'Fertilização para desenvolvimento da planta',
        daysFromStart: 60,
        order: 3,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't4',
        name: 'Controle de plantas daninhas',
        description: 'Capina ou aplicação de herbicida',
        daysFromStart: 90,
        order: 4,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't5',
        name: 'Aplicação de fungicida',
        description: 'Controle preventivo de ferrugem',
        daysFromStart: 180,
        order: 5,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't6',
        name: 'Colheita (após 2-3 anos)',
        description: 'Primeira colheita quando frutos estiverem maduros',
        daysFromStart: 730,
        order: 6,
        status: 'pendente',
        taskType: 'colheita',
      },
    ],
    // AGC: Generic plan for other crops
    outros: [
      {
        id: 't1',
        name: 'Preparo de solo',
        description: 'Preparação do solo para plantio',
        daysFromStart: -15,
        order: 1,
        status: 'pendente',
        taskType: 'preparo',
      },
      {
        id: 't2',
        name: 'Plantio/Semeadura',
        description: `Plantio de ${cropName}`,
        daysFromStart: 0,
        order: 2,
        status: 'pendente',
        taskType: 'plantio',
      },
      {
        id: 't3',
        name: 'Adubação',
        description: 'Aplicação de fertilizantes',
        daysFromStart: 30,
        order: 3,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't4',
        name: 'Controle de pragas e doenças',
        description: 'Aplicação de defensivos conforme necessário',
        daysFromStart: 60,
        order: 4,
        status: 'pendente',
        taskType: 'aplicacao',
      },
      {
        id: 't5',
        name: 'Colheita',
        description: `Colheita de ${cropName}`,
        daysFromStart: 120,
        order: 5,
        status: 'pendente',
        taskType: 'colheita',
      },
    ],
    // AGC: Use soja plan as template for missing crops
    algodao: [],
    cana: [],
  }

  // AGC: Return plan for crop type, or generic plan
  return plans[cropType] || plans.outros
}


